import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, switchMap, throwError } from 'rxjs';

import { AuthService } from '../services/auth.service';
import { ToastService } from '../services/toast.service';
import { UserStatus } from '../models';

interface RetryableRequest extends HttpRequest<unknown> {
  _retry?: boolean;
}

const shouldBypassAuth = (url: string): boolean => {
  const normalizedUrl = url.replace(/^https?:\/\/[^/]+/i, '');
  return /(^|\/)auth\//.test(normalizedUrl);
};

const getAccountStatusFromError = (error: HttpErrorResponse): UserStatus | null => {
  const status = error.error?.user?.status;
  if (status === 'pending' || status === 'declined') {
    return status;
  }

  const message = String(error.error?.message ?? error.message ?? '').toLowerCase();

  if (message.includes('account pending validation')) {
    return 'pending';
  }

  if (message.includes('account declined')) {
    return 'declined';
  }

  return null;
};

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthService);
  const toastService = inject(ToastService);
  const token = authService.accessToken();
  const isAuthRequest = shouldBypassAuth(req.url);

  const requestWithAuth = !isAuthRequest && token
    ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
    : req;
  const original = requestWithAuth as RetryableRequest;

  return next(original).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse) {
        const blockedStatus = getAccountStatusFromError(error);

        if (blockedStatus) {
          const message = blockedStatus === 'pending'
            ? 'Tu cuenta está pendiente de validación. Un superadministrador debe activarla.'
            : 'Tu cuenta fue declinada. Comunícate con un superadministrador para más información.';
          toastService.show(message, 'error');
          authService.logout();
          return throwError(() => error);
        }
      }

      if (!(error instanceof HttpErrorResponse) || error.status !== 401 || isAuthRequest) {
        return throwError(() => error);
      }

      if (original._retry) {
        authService.logout(true);
        return throwError(() => error);
      }

      return authService.refresh().pipe(
        switchMap((tokens) => {
          if (!tokens?.accessToken) {
            authService.logout(true);
            return throwError(() => error);
          }

          const retryRequest = original.clone({
            setHeaders: { Authorization: `Bearer ${tokens.accessToken}` }
          }) as RetryableRequest;
          retryRequest._retry = true;

          return next(retryRequest);
        }),
        catchError((refreshError) => {
          if (refreshError instanceof HttpErrorResponse) {
            const blockedStatus = getAccountStatusFromError(refreshError);

            if (blockedStatus) {
              const message = blockedStatus === 'pending'
                ? 'Tu cuenta está pendiente de validación. Un superadministrador debe activarla.'
                : 'Tu cuenta fue declinada. Comunícate con un superadministrador para más información.';
              toastService.show(message, 'error');
            }
          }

          authService.logout(true);
          return throwError(() => refreshError);
        })
      );
    })
  );
};
